/* eslint-disable */
import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Fade } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
// Donut chart
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
// Tooltip
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { formatNumber } from "utils/formatNumberUtils";
// Icons
import { Assessment, Pause } from "@material-ui/icons";
import AuthContext from "contexts/AuthProvider.js";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    // Se quiser manter um estilo base de container
    // Pode renomear para "containerRoot"
    width: "100%",
    height: "100%",
    position: "relative",
  },
  content: {
    display: "flex",
    flexDirection: "center",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
    height: "100%",
  },
  chartContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  chartWrapper: {
    width: 60,
    height: 60,
    marginRight: theme.spacing(2),
    cursor: "pointer",
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    margin: theme.spacing(0, 0.3),
    transition: "background-color 0.5s",
    cursor: "pointer",
  },
  footerBox: {
    paddingTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  pauseIcon: {
    position: "absolute",
    bottom: "5px",
    right: "5px",
    color: "#999",
    cursor: "default",
    opacity: 0.8,
  },
}));

function KpiRotatingCard({ kpisData }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (kpisData.length <= 1 || isPaused) return;
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % kpisData.length);
      setFadeKey((oldKey) => oldKey + 1);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [kpisData.length, isPaused]);

  if (!kpisData.length) {
    return <div>{t("whatshappening.messages.no_kpi")}</div>;
  }

  const currentKPI = kpisData[currentIndex];

  const percentage =
    currentKPI.meta === 0
      ? 0
      : Math.round((currentKPI.real / currentKPI.meta) * 100);

  const metaFormatted = formatNumber(currentKPI.meta, currentKPI.unit, true);
  const realFormatted = formatNumber(currentKPI.real, currentKPI.unit, true);

  return (
    <div
      className={classes.cardRoot}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Container do donut e nome */}
      <Box className={classes.content}>
        <Fade key={fadeKey} in timeout={500}>
          <Box>
            <Box className={classes.chartContainer}>
              <Tippy
                content={`Meta: ${metaFormatted} | Real: ${realFormatted}`}
                arrow={true}
                theme="light"
              >
                <div className={classes.chartWrapper}>
                  <CircularProgressbar
                    value={percentage}
                    text={`${percentage}%`}
                    strokeWidth={10}
                    styles={buildStyles({
                      textSize: "25px",
                      textColor: "#666",
                      pathColor: clientColor,
                      trailColor: "#e0e0e0",
                      backgroundColor: "#fff",
                    })}
                  />
                </div>
              </Tippy>

              {/* Nome do KPI + Valor */}
              <Box>
                <Typography
                  variant="subtitle1"
                  style={{ fontSize: "15px", fontWeight: 800, marginBottom: 4 }}
                >
                  {currentKPI.kpiName}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ fontSize: "35px", color: "#555" }}
                >
                  {realFormatted}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Pontinhos de navegação */}
      <Box className={classes.footerBox}>
        <Box className={classes.dotsContainer}>
          {kpisData.map((_, i) => (
            <Box
              key={i}
              className={classes.dot}
              style={{
                backgroundColor: i === currentIndex ? clientColor : "#ccc",
              }}
              onClick={() => {
                setCurrentIndex(i);
                setFadeKey((old) => old + 1);
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Ícone de pause */}
      <Fade in={isPaused} timeout={500}>
        <Box className={classes.pauseIcon}>
          <Pause fontSize="small" />
        </Box>
      </Fade>
    </div>
  );
}

KpiRotatingCard.propTypes = {
  kpisData: PropTypes.arrayOf(
    PropTypes.shape({
      kpiName: PropTypes.string,
      meta: PropTypes.number,
      real: PropTypes.number,
    })
  ),
};

KpiRotatingCard.defaultProps = {
  kpisData: [],
};

export default KpiRotatingCard;
